import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import ProductCard from '../components/ProductCard';

const categoryIcons = {
  'Jewelry': '💎',
  'Parure': '👑',
  'Bracelets': '📿',
  'Featured Collection': '✨',
  'Dresses & Tops': '👗',
  'Bags & Wallets': '👜',
  'Hair Accessories': '🎀',
  'Keychains & Brooches': '🔑',
  'Kitchen': '🍳',
  'Shoes & Footwear': '👟',
  'Toys & Games': '🧸',
  'Baby & Kids': '🍼',
  'Home Decor': '🏠',
  'Storage & Organization': '🗂️',
  'Beauty & Nails': '💅',
  'Scarves & Accessories': '🧣',
  'Party Supplies': '🎈',
  'Swimwear': '👙',
  'Crafts & DIY': '🧵',
  'Automotive': '🚗',
  'Sports & Outdoors': '⚽',
  'Stationery': '✏️',
  'Furniture': '🛋️',
};

const SLIDES = [
  {
    gradient: 'linear-gradient(135deg,#1e1b4b 0%,#312e81 30%,#4c1d95 60%,#5b21b6 100%)',
    blob1: '#7c3aed', blob2: '#be185d', blob3: '#0891b2',
    pills: ['💎 Jewelry','👗 Fashion','👜 Bags','📿 Bracelets','🎀 Accessories'],
    title: 'Your Style,', accent: 'Your Rules.',
    sub: 'Discover thousands of handpicked jewelry, dresses & accessories — all in one place.',
    cta: 'Shop Now →', shopCat: null,
    cta2: '✨ Featured Collection', shopCat2: 'Featured Collection',
  },
  {
    gradient: 'linear-gradient(135deg,#1a1a2e 0%,#16213e 40%,#0f3460 100%)',
    blob1: '#2563eb', blob2: '#7c3aed', blob3: '#06b6d4',
    pills: ['💎 Necklaces','📿 Bracelets','💍 Rings','👂 Earrings'],
    title: 'Fresh Jewelry.', accent: 'Endless Sparkle.',
    sub: 'Explore our stunning jewelry collection — from elegant necklaces to bold statement pieces.',
    cta: 'Shop Jewelry →', shopCat: 'Jewelry',
    cta2: '✨ Featured Picks', shopCat2: 'Featured Collection',
  },
  {
    gradient: 'linear-gradient(135deg,#3b0764 0%,#6b21a8 40%,#a21caf 100%)',
    blob1: '#ec4899', blob2: '#a855f7', blob3: '#f43f5e',
    pills: ['👗 Dresses','👚 Tops','👖 Bottoms','🧥 Outerwear'],
    title: 'Fashion That', accent: 'Fits Your Vibe.',
    sub: 'From casual chic to evening elegance — find your perfect look in our fashion collection.',
    cta: 'Shop Fashion →', shopCat: 'Dresses & Tops',
    cta2: 'View New Arrivals', shopCat2: null,
  },
  {
    gradient: 'linear-gradient(135deg,#064e3b 0%,#065f46 40%,#047857 100%)',
    blob1: '#10b981', blob2: '#0891b2', blob3: '#7c3aed',
    pills: ['👜 Handbags','💼 Totes','👝 Crossbody','💛 Clutches'],
    title: 'Carry It', accent: 'With Style.',
    sub: 'Statement bags & wallets that complete every outfit — shop our curated bag collection.',
    cta: 'Shop Bags →', shopCat: 'Bags & Wallets',
    cta2: '👜 View All Bags', shopCat2: 'Bags & Wallets',
  },
];


const categoryGradients = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
  'linear-gradient(135deg,#a18cd1,#fbc2eb)',
  'linear-gradient(135deg,#fccb90,#d57eeb)',
  'linear-gradient(135deg,#a1c4fd,#c2e9fb)',
  'linear-gradient(135deg,#fd7043,#ff8a65)',
  'linear-gradient(135deg,#26c6da,#00acc1)',
  'linear-gradient(135deg,#66bb6a,#43a047)',
  'linear-gradient(135deg,#ef5350,#e53935)',
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slide, setSlide] = useState(0);

  const next = useCallback(() => setSlide(s => (s + 1) % SLIDES.length), []);
  const prev = () => setSlide(s => (s - 1 + SLIDES.length) % SLIDES.length);

  useEffect(() => { const t = setInterval(next, 5000); return () => clearInterval(t); }, [next]);

  useEffect(() => {
    Promise.all([
      api.get('/products?limit=16&sort=newest'),
      api.get('/categories')
    ]).then(([prodRes, catRes]) => {
      setProducts(prodRes.data.products);
      setCategories(catRes.data);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="spinner" />;

  const s = SLIDES[slide];
  const getCatSlug = (name) => name ? (categories.find(c => c.name === name)?.slug || '') : '';
  const featuredSlug = getCatSlug('Featured Collection');

  return (
    <div>
      {/* ── Hero Carousel ── */}
      <div className="hero-banner" style={{ background: s.gradient }}>
        <div className="hero-shapes">
          <div className="hs hs1" style={{ background: s.blob1 }}/>
          <div className="hs hs2" style={{ background: s.blob2 }}/>
          <div className="hs hs3" style={{ background: s.blob3 }}/>
        </div>
        <button className="hero-arrow hero-arrow-l" onClick={prev}>‹</button>
        <div className="hero-content">
          <div className="hero-cats">
            {s.pills.map(t => <span key={t} className="hero-cat-pill">{t}</span>)}
          </div>
          <h1 className="hero-title">{s.title}<br/><span className="hero-accent">{s.accent}</span></h1>
          <p className="hero-subtitle">{s.sub}</p>
          <div className="hero-cta">
            <Link to={s.shopCat ? `/shop?category=${getCatSlug(s.shopCat)}` : '/shop'} className="btn-hero-primary">{s.cta}</Link>
            <Link to={s.shopCat2 ? `/shop?category=${getCatSlug(s.shopCat2)}` : '/shop'} className="btn-hero-outline">{s.cta2}</Link>
          </div>
        </div>
        <button className="hero-arrow hero-arrow-r" onClick={next}>›</button>
        <div className="hero-dots">
          {SLIDES.map((_, i) => (
            <button key={i} className={`hero-dot${i === slide ? ' active' : ''}`} onClick={() => setSlide(i)} />
          ))}
        </div>
      </div>

      {/* ── Trust badges strip ── */}
      <div style={{ background:'white', borderBottom:'1px solid #f3f4f6', borderTop:'1px solid #f3f4f6' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'16px 24px', display:'flex', justifyContent:'center', flexWrap:'wrap', gap:'8px 24px' }}>
          {[
            { icon:'🔒', text:'Secure Checkout', sub:'SSL encrypted' },
            { icon:'🚚', text:'Free Shipping', sub:'On orders over $50' },
            { icon:'↩️', text:'Easy Returns', sub:'30-day hassle-free' },
            { icon:'💳', text:'Safe Payments', sub:'Stripe protected' },
            { icon:'💎', text:'VIP Savings', sub:'10% off every order' },
            { icon:'⭐', text:'10,000+ Products', sub:'New arrivals daily' },
          ].map((b, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0' }}>
              <span style={{ fontSize:22 }}>{b.icon}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:'#111', lineHeight:1.2 }}>{b.text}</div>
                <div style={{ fontSize:11, color:'#9ca3af' }}>{b.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="container">
        <h2 className="section-title">Shop by Category</h2>

      {/* VIP Membership Banner */}
      <Link to="/membership" style={{ display:'block', textDecoration:'none', marginBottom:32 }}>
        <div style={{ background:'linear-gradient(135deg,#1e1b4b,#4c1d95,#4f46e5)', borderRadius:16, padding:'24px 32px', color:'white', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <span style={{ fontSize:40 }}>💎</span>
            <div>
              <div style={{ fontWeight:900, fontSize:18, marginBottom:4 }}>StyleMe VIP Membership</div>
              <div style={{ opacity:0.85, fontSize:14 }}>10% off every order · Free shipping · Early access · Birthday discount</div>
            </div>
          </div>
          <div style={{ background:'white', color:'#4f46e5', padding:'10px 24px', borderRadius:100, fontWeight:800, fontSize:14, whiteSpace:'nowrap' }}>From $9.99/mo →</div>
        </div>
      </Link>
      {(() => {
        const cats = categories.filter(cat => cat.slug !== 'uncategorized' && cat.product_count > 0);
        const catCard = (cat, i, isBig) => {
          const imgUrl = cat.cover_image
            ? (cat.cover_image.startsWith('http') ? cat.cover_image : `${process.env.REACT_APP_API_URL || ''}${cat.cover_image}`)
            : null;
          return (
            <Link to={`/shop?category=${cat.slug}`} key={cat.id} style={{ textDecoration:'none', display:'block', ...(isBig ? {} : {}) }}>
              <div style={{ position:'relative', borderRadius:20, overflow:'hidden', height: isBig ? '100%' : undefined, minHeight: isBig ? 460 : 220, background:categoryGradients[i % categoryGradients.length], cursor:'pointer', boxShadow:'0 4px 16px rgba(0,0,0,0.12)' }}
                onMouseEnter={e => { const img = e.currentTarget.querySelector('.cat-img'); if (img) img.style.transform='scale(1.08)'; e.currentTarget.style.boxShadow='0 12px 32px rgba(0,0,0,0.22)'; }}
                onMouseLeave={e => { const img = e.currentTarget.querySelector('.cat-img'); if (img) img.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.12)'; }}>
                {imgUrl && <img className="cat-img" src={imgUrl} alt={cat.name} style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.4s ease' }} />}
                <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.08) 55%, transparent 100%)' }}/>
                <div style={{ position:'absolute', top:12, right:12, background:'rgba(255,255,255,0.92)', color:'#111', fontSize:10, fontWeight:800, padding:'4px 12px', borderRadius:100, letterSpacing:0.5 }}>SHOP →</div>
                <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:isBig ? '20px 20px 24px' : '14px 14px 16px' }}>
                  <div style={{ color:'white', fontWeight:900, fontSize: isBig ? 20 : 14, lineHeight:1.3, marginBottom: isBig ? 10 : 0 }}>{cat.name}</div>
                  {isBig && <Link to={`/shop?category=${cat.slug}`} style={{ background:'white', color:'#111', padding:'8px 20px', borderRadius:100, fontWeight:700, fontSize:13, textDecoration:'none', display:'inline-block' }} onClick={e => e.stopPropagation()}>Shop Now →</Link>}
                </div>
              </div>
            </Link>
          );
        };
        return (
          <>
            {/* Editorial top 5 */}
            {cats.length >= 5 && (
              <div className="lifestyle-grid" style={{ marginBottom:12 }}>
                <div className="lifestyle-big">{catCard(cats[0], 0, true)}</div>
                {catCard(cats[1], 1, false)}
                {catCard(cats[2], 2, false)}
                {catCard(cats[3], 3, false)}
                {catCard(cats[4], 4, false)}
              </div>
            )}
            {/* Remaining categories */}
            {cats.length > 5 && (
              <div className="categories-grid" style={{ marginBottom:48 }}>
                {cats.slice(5).map((cat, i) => catCard(cat, i + 5, false))}
              </div>
            )}
          </>
        );
      })()}

      {/* ── Style Inspiration ── */}
      <div style={{ marginBottom: 56 }}>
        <h2 className="section-title">Style Inspiration</h2>
        <p style={{ color:'#6b7280', marginBottom:24, fontSize:15, textAlign:'center' }}>Real looks, real style. Get inspired and shop the pieces you love.</p>
        <div className="lifestyle-grid">
          {/* Big left card */}
          <div className="lifestyle-big" style={{ position:'relative', borderRadius:20, overflow:'hidden', minHeight:480 }}>
            <img src="/lifestyle-fashion.jpg" alt="Fashion style" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 50%)' }}/>
            <div style={{ position:'absolute', bottom:24, left:24, right:24 }}>
              <div style={{ color:'white', fontWeight:900, fontSize:20, marginBottom:8 }}>Dress to Impress</div>
              <Link to="/shop?category=dresses-tops" style={{ background:'white', color:'#111', padding:'8px 20px', borderRadius:100, fontWeight:700, fontSize:13, textDecoration:'none', display:'inline-block' }}>Shop Dresses →</Link>
            </div>
          </div>
          {/* Top right */}
          <div style={{ position:'relative', borderRadius:20, overflow:'hidden', minHeight:232 }}>
            <img src="/lifestyle-jewelry.jpg" alt="Jewelry style" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)' }}/>
            <div style={{ position:'absolute', bottom:16, left:16, right:16 }}>
              <div style={{ color:'white', fontWeight:800, fontSize:14, marginBottom:6 }}>Jewelry Sets</div>
              <Link to="/shop?category=jewelry" style={{ background:'white', color:'#111', padding:'6px 14px', borderRadius:100, fontWeight:700, fontSize:12, textDecoration:'none', display:'inline-block' }}>Shop →</Link>
            </div>
          </div>
          {/* Top far right */}
          <div style={{ position:'relative', borderRadius:20, overflow:'hidden', minHeight:232 }}>
            <img src="/lifestyle-bags.jpg" alt="Bag style" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)' }}/>
            <div style={{ position:'absolute', bottom:16, left:16, right:16 }}>
              <div style={{ color:'white', fontWeight:800, fontSize:14, marginBottom:6 }}>Bags & Wallets</div>
              <Link to="/shop?category=bags-wallets" style={{ background:'white', color:'#111', padding:'6px 14px', borderRadius:100, fontWeight:700, fontSize:12, textDecoration:'none', display:'inline-block' }}>Shop →</Link>
            </div>
          </div>
          {/* Bottom right */}
          <div style={{ position:'relative', borderRadius:20, overflow:'hidden', minHeight:232 }}>
            <img src="/lifestyle-summer.jpg" alt="Summer fashion" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)' }}/>
            <div style={{ position:'absolute', bottom:16, left:16, right:16 }}>
              <div style={{ color:'white', fontWeight:800, fontSize:14, marginBottom:6 }}>Summer Looks</div>
              <Link to="/shop?search=summer" style={{ background:'white', color:'#111', padding:'6px 14px', borderRadius:100, fontWeight:700, fontSize:12, textDecoration:'none', display:'inline-block' }}>Shop →</Link>
            </div>
          </div>
          {/* Bottom far right */}
          <div style={{ position:'relative', borderRadius:20, overflow:'hidden', minHeight:232 }}>
            <img src="/lifestyle-accessories.jpg" alt="Accessories" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)' }}/>
            <div style={{ position:'absolute', bottom:16, left:16, right:16 }}>
              <div style={{ color:'white', fontWeight:800, fontSize:14, marginBottom:6 }}>Accessories</div>
              <Link to="/shop" style={{ background:'white', color:'#111', padding:'6px 14px', borderRadius:100, fontWeight:700, fontSize:12, textDecoration:'none', display:'inline-block' }}>Shop →</Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Social proof stats ── */}
      <div style={{ background:'linear-gradient(135deg,#f8f7ff,#fdf4ff)', borderRadius:20, padding:'40px 32px', marginBottom:48, textAlign:'center' }}>
        <h2 style={{ fontSize:24, fontWeight:900, marginBottom:8 }}>Why Women Love StyleMe</h2>
        <p style={{ color:'#6b7280', marginBottom:32, fontSize:15 }}>Thousands of happy shoppers. Here's why they keep coming back.</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:20, maxWidth:800, margin:'0 auto 36px' }}>
          {[
            { stat:'10,000+', label:'Products', icon:'🛍️' },
            { stat:'4.8★', label:'Avg Rating', icon:'⭐' },
            { stat:'$35', label:'Avg Order Value', icon:'💰' },
            { stat:'30 Days', label:'Free Returns', icon:'↩️' },
          ].map((s, i) => (
            <div key={i} style={{ background:'white', borderRadius:16, padding:'24px 16px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize:28, marginBottom:8 }}>{s.icon}</div>
              <div style={{ fontSize:28, fontWeight:900, color:'#4f46e5', marginBottom:4 }}>{s.stat}</div>
              <div style={{ fontSize:13, color:'#6b7280', fontWeight:600 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {/* Customer quote */}
        <div style={{ maxWidth:560, margin:'0 auto', background:'white', borderRadius:16, padding:'24px 28px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize:36, color:'#4f46e5', lineHeight:1, marginBottom:8 }}>"</div>
          <p style={{ fontSize:15, color:'#374151', lineHeight:1.7, margin:'0 0 16px', fontStyle:'italic' }}>
            Absolutely love StyleMe! The jewelry quality is incredible for the price, and I received my order faster than expected. The VIP membership pays for itself in one order.
          </p>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#4f46e5,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:700, fontSize:14 }}>S</div>
            <div style={{ textAlign:'left' }}>
              <div style={{ fontWeight:700, fontSize:13 }}>Sarah M.</div>
              <div style={{ fontSize:11, color:'#f59e0b' }}>⭐⭐⭐⭐⭐ Verified Buyer</div>
            </div>
          </div>
        </div>
      </div>

      <h2 className="section-title">Latest Products</h2>
      {products.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📦</div>
          <h3>No products yet</h3>
          <p>Products will appear here once added to the inventory.</p>
        </div>
      ) : (
        <div className="products-grid">
          {products.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
      </div>
    </div>
  );
}
